import React, { useState } from "react";
import { useGame } from "@/contexts/GameContext";
import useTabMusic from "@/hooks/useTabMusic";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Users, Package, Sparkles, Shield, Sword, Heart, User } from "lucide-react";
import { toast } from "sonner";
import GameCard from "./GameCard";
import { t } from "@/utils/i18n";

export default function Inventory() {
  const { game, equipItem, unequipItem } = useGame();
  const [activeTab, setActiveTab] = useState("allies");
  useTabMusic('inventory');

  if (!game) return null;

  const lang = game.language || "en";
  const inventory = game.inventory || { allies: [], objects: [], abilities: [] };
  const character = game.character;
  const equipped = character.equipped || {};
  const inBattle = game.battle_state && game.battle_state.active;

  const handleEquip = async (cardId, slot) => {
    try {
      await equipItem(game.id, cardId, slot);
      toast.success(t(lang, 'equip') + "!");
    } catch {
      toast.error("Failed to equip");
    }
  };

  const handleUnequip = async (cardId, slot) => {
    try {
      await unequipItem(game.id, cardId, slot);
      toast.success(t(lang, 'unequip'));
    } catch {
      toast.error("Failed to unequip");
    }
  };

  return (
    <div className="py-2 sm:py-6 px-2 sm:px-4" data-testid="inventory-view">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
          {/* Character Equipment Panel - compact on mobile */}
          <div className="lg:col-span-1">
            <div className="bg-[#1a1a1d]/80 border border-[#cfb53b]/20 rounded-lg p-3 sm:p-5" data-testid="character-equipment">
              <h3 className="font-heading text-base sm:text-lg text-[#cfb53b] mb-3 flex items-center gap-2">
                <User size={16} /> {t(lang, 'equipment')}
              </h3>

              {/* Stats - compact grid on mobile */}
              <div className="grid grid-cols-4 sm:grid-cols-2 gap-2 mb-3 sm:mb-5">
                <div className="bg-[#0a0a0c] rounded p-1.5 sm:p-2 text-center">
                  <Heart size={12} className="mx-auto text-red-500 mb-0.5" />
                  <p className="text-[10px] text-[#94a3b8]">{t(lang, 'hp')}</p>
                  <p className="font-heading text-xs sm:text-sm text-[#e2e8f0]">{character.hp}/{character.max_hp}</p>
                </div>
                <div className="bg-[#0a0a0c] rounded p-1.5 sm:p-2 text-center">
                  <Sparkles size={12} className="mx-auto text-blue-400 mb-0.5" />
                  <p className="text-[10px] text-[#94a3b8]">{t(lang, 'mana')}</p>
                  <p className="font-heading text-xs sm:text-sm text-[#e2e8f0]">{character.mana}/{character.max_mana}</p>
                </div>
                <div className="bg-[#0a0a0c] rounded p-1.5 sm:p-2 text-center">
                  <Sword size={12} className="mx-auto text-[#cfb53b] mb-0.5" />
                  <p className="text-[10px] text-[#94a3b8]">{t(lang, 'atk')}</p>
                  <p className="font-heading text-xs sm:text-sm text-[#e2e8f0]">
                    {character.effective_attack ?? character.base_attack}
                    {character.equip_bonus_atk > 0 && (
                      <span className="text-[9px] text-[#cfb53b] ml-0.5">(+{character.equip_bonus_atk})</span>
                    )}
                  </p>
                </div>
                <div className="bg-[#0a0a0c] rounded p-1.5 sm:p-2 text-center">
                  <Shield size={12} className="mx-auto text-blue-400 mb-0.5" />
                  <p className="text-[10px] text-[#94a3b8]">{t(lang, 'def')}</p>
                  <p className="font-heading text-xs sm:text-sm text-[#e2e8f0]">
                    {character.effective_defense ?? character.base_defense}
                    {character.equip_bonus_def > 0 && (
                      <span className="text-[9px] text-blue-400 ml-0.5">(+{character.equip_bonus_def})</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Equipment Slots - horizontal on mobile */}
              <div className="grid grid-cols-3 sm:grid-cols-1 gap-2 sm:gap-3">
                {[
                  { slot: "weapon", label: t(lang, 'weapon') },
                  { slot: "armor", label: t(lang, 'armor_slot') },
                  { slot: "accessory", label: t(lang, 'accessory') },
                ].map(({ slot, label }) => {
                  const item = equipped[slot];
                  return (
                    <div key={slot} className="bg-[#0a0a0c] border border-[#2d3748] rounded-lg p-2 sm:p-3" data-testid={`equip-slot-${slot}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] sm:text-xs text-[#94a3b8] capitalize font-heading">{label}</span>
                        {item && (
                          <Button
                            data-testid={`unequip-${slot}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnequip(item.id, slot)}
                            className="text-[10px] text-[#8b0000] hover:text-red-400 h-5 px-1"
                          >
                            {t(lang, 'unequip')}
                          </Button>
                        )}
                      </div>
                      {item ? (
                        <div className="mt-0.5">
                          <p className="font-heading text-[10px] sm:text-sm text-[#cfb53b] truncate">{item.name}</p>
                          <div className="flex gap-1.5 mt-0.5">
                            {item.attack > 0 && <span className="text-[9px] text-red-400">+{item.attack}</span>}
                            {item.defense > 0 && <span className="text-[9px] text-blue-400">+{item.defense}</span>}
                          </div>
                        </div>
                      ) : (
                        <p className="text-[10px] text-[#64748b] mt-0.5 italic">{t(lang, 'empty_slot')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Card Collection */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="bg-[#1a1a1d] border border-[#cfb53b]/20 w-full grid grid-cols-3">
                <TabsTrigger
                  value="allies"
                  data-testid="inventory-tab-allies"
                  className="font-heading text-sm data-[state=active]:bg-[#064e3b]/30 data-[state=active]:text-emerald-400"
                >
                  <Users size={14} className="mr-1.5" />
                  {t(lang, 'allies')} ({inventory.allies?.length || 0})
                </TabsTrigger>
                <TabsTrigger
                  value="objects"
                  data-testid="inventory-tab-objects"
                  className="font-heading text-sm data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b]"
                >
                  <Package size={14} className="mr-1.5" />
                  {t(lang, 'objects')} ({inventory.objects?.length || 0})
                </TabsTrigger>
                <TabsTrigger
                  value="abilities"
                  data-testid="inventory-tab-abilities"
                  className="font-heading text-sm data-[state=active]:bg-[#581c87]/20 data-[state=active]:text-purple-400"
                >
                  <Sparkles size={14} className="mr-1.5" />
                  {t(lang, 'abilities')} ({inventory.abilities?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="allies" className="mt-4">
                {inventory.allies?.length === 0 ? (
                  <EmptyState text={t(lang, 'no_allies')} icon={Users} />
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3 stagger-children">
                    {inventory.allies.map(card => (
                      <GameCard key={card.id} card={card} type="ally" inBattle={inBattle} variant="compact" />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="objects" className="mt-4">
                {inventory.objects?.length === 0 ? (
                  <EmptyState text={t(lang, 'no_objects')} icon={Package} />
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3 stagger-children">
                    {inventory.objects.map(card => (
                      <GameCard key={card.id} card={card} type="object" onEquip={handleEquip} inBattle={inBattle} variant="compact" />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="abilities" className="mt-4">
                {inventory.abilities?.length === 0 ? (
                  <EmptyState text={t(lang, 'no_abilities')} icon={Sparkles} />
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3 stagger-children">
                    {inventory.abilities.map(card => (
                      <GameCard key={card.id} card={card} type="ability" inBattle={inBattle} variant="compact" />
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text, icon: Icon }) {
  return (
    <div className="text-center py-12 border border-dashed border-[#2d3748] rounded-lg">
      <Icon size={40} className="mx-auto text-[#cfb53b]/20 mb-3" />
      <p className="text-[#94a3b8] text-sm">{text}</p>
    </div>
  );
}
